import { cn } from '@utils/cn';
import React, { useId } from 'react';

import { Alert } from '../Alert';
import { Button } from '../Button';
import styles from './alertDialog.module.css';

export interface AlertDialogProps {
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  closeLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  onClose: () => void;
  loading?: boolean;
  error?: string;
  className?: string;
}

export const AlertDialog = ({
  cancelLabel,
  className,
  closeLabel,
  confirmLabel,
  description,
  error,
  loading = false,
  onCancel,
  onClose,
  onConfirm,
  title,
}: AlertDialogProps) => {
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;

  return (
    <div
      aria-describedby={descriptionId}
      aria-labelledby={titleId}
      className={cn(styles.UiAlertDialog, className)}
      role="alertdialog"
    >
      <div className="grid grid-cols-[2.25rem_1fr_2.25rem] items-center gap-2">
        <span />
        <h2
          className="text-balance text-center text-sm font-bold md:text-md"
          id={titleId}
        >
          {title}
        </h2>
        <Button
          disabled={loading}
          hideLabel
          icon="ic_close"
          label={closeLabel}
          onClick={onClose}
          size="sm"
          variant="transparent"
        />
      </div>

      <div
        className="text-pretty text-center text-sm-mobile md:text-sm"
        id={descriptionId}
      >
        {description}
      </div>

      {error ? <Alert status="error">{error}</Alert> : null}

      <div className="grid grid-cols-2 gap-3">
        <Button
          disabled={loading}
          fullWidth
          label={cancelLabel}
          onClick={onCancel}
          variant="secondary"
        />
        <Button
          fullWidth
          label={confirmLabel}
          loading={loading}
          onClick={onConfirm}
          variant="primary"
        />
      </div>
    </div>
  );
};
